import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { FC, ReactElement, useCallback } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { ReactComponent as CheckCircleOutlineIcon } from '~/ui/assets/images/check_circle_outline.svg';
import { ReactComponent as DeleteIcon } from '~/ui/assets/images/delete.svg';
import smallAvatarIcon from '~/ui/assets/images/smallAvatar.svg';
import variables from '~/ui/assets/styles/colors.module.scss';
import ButtonIcon from '~/ui/components/common/ButtonIcon';
import PrivateImage from '~/ui/components/common/PrivateImage';
import FollowUpTypography from '~/ui/pages/Network/components/FollowUpTypography';
import { IFormValues } from '../types';
import MemoField from './MemoField';

import styles from '../EditCrisisAllocation.module.scss';

interface IProps {
  allocationIdx: number;
}

const AllocationClientsList: FC<IProps> = ({ allocationIdx }): ReactElement | null => {
  const { control, setValue, watch } = useFormContext<IFormValues>();
  const changedAllocationsIds = watch('changedAllocationsIds') || [];
  const deletedAllocationsIds = watch('deletedAllocationsIds') || [];

  const { fields, remove } = useFieldArray({
    control,
    name: `allocations.${allocationIdx}.clients` as const,
  });

  const onChangeValuesCallback = useCallback(
    (allocationId: number) => {
      setValue(
        'changedAllocationsIds',
        Array.from(new Set([...changedAllocationsIds, allocationId])),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [changedAllocationsIds],
  );

  const handleRemoveClient = (clientIdx: number, allocationId: number) => {
    setValue(
      'deletedAllocationsIds',
      Array.from(new Set([...deletedAllocationsIds, allocationId])),
    );
    remove(clientIdx);
  };

  if (!fields.length) return null;

  return (
    <>
      <Typography variant="subtitle1" className={styles.assignedClientsTitle}>
        Assigned Clients
      </Typography>
      {fields.map((field, clientIdx) => {
        const { allocation, photo, firstName, lastName } = field as typeof field & {
          allocation: { id: number; resolved: boolean; isFollowUp: boolean };
          photo: string;
          firstName: string;
          lastName: string;
        };

        return (
          <Grid container key={field.id} alignItems="center" className={styles.clientDetailItem}>
            <Grid item xs={6} className={styles.selectedClient}>
              <PrivateImage
                disabled={allocation?.resolved}
                className={styles.image}
                src={photo || smallAvatarIcon}
                height={40}
                alt="avatar"
                additionalIcon={
                  allocation?.resolved ? (
                    <div className={styles.additionalIcon}>
                      <CheckCircleOutlineIcon width={16} height={16} />
                    </div>
                  ) : undefined
                }
              />
              <Typography>
                {firstName} {lastName}
              </Typography>
              {allocation?.isFollowUp && <FollowUpTypography />}
            </Grid>
            <Grid item xs={6} className={styles.memoContainer}>
              <MemoField
                memoFieldName={`allocations.${allocationIdx}.clients.${clientIdx}.memo`}
                disabled={allocation?.resolved || allocation?.isFollowUp}
                onMemoChange={() => onChangeValuesCallback(field.allocation.id)}
              />
              <ButtonIcon
                onClick={() => handleRemoveClient(clientIdx, field.allocation.id)}
                disabled={allocation?.resolved || allocation?.isFollowUp}
              >
                <DeleteIcon width={20} color={variables.colorRed} />
              </ButtonIcon>
            </Grid>
          </Grid>
        );
      })}
    </>
  );
};

export default AllocationClientsList;
